import { useData } from "@hooks/useData";
import { components, paths } from "@apiSchema";
import http from "@core/fetch";
import apiConfig from "@apiConfig";
import { iPermissionFields, PermissionLevelEnum, PermissionSection } from "@models/PermissionSection";
import { ISocialPost, SocialPosts as SocialPostsModel } from "@models/SocialPosts";
import { SocialKeywordTypeEnum } from "@enums";
import { v4 as uuid } from "uuid";

type Response = components["schemas"]["SocialThemesResponseViewModelChainPermissionWrapper"];
type SocialTheme = components["schemas"]["SocialThemeViewModel"];
type SocialThemePost = components["schemas"]["SocialThemePostViewModel"];
type SocialThemeKeyword = components["schemas"]["SocialThemeKeywordViewModel"];
type RequestParams = NonNullable<paths["/api/WhatsHot/GetThemes"]["get"]["parameters"]["query"]>;

export interface IConversationTheme {
    id: number;
    name: string;
    score: number;
}

export interface IKeyTerm {
    id: number;
    name: string;
    thumbnailUrl: string;
    chainId: string | null;
}

export interface SocialThemesData extends iPermissionFields {
    themes: IConversationTheme[];
    postsByTheme: Record<number, { byEngagement: ISocialPost[]; byDate: ISocialPost[] }>;
    keywordsByTheme: Record<number, IKeyTerm[]>;
}

const REQUEST_TYPE: keyof typeof apiConfig = 'get social themes';

const FAKE_THEMES: IConversationTheme[] = [
    { id: 1, name: 'Quick And Easy Weeknight Dinner Ideas', score: 95 },
    { id: 2, name: 'Healthy Smoothie Bowls For Breakfast', score: 80 },
    { id: 3, name: 'Classic Homemade Bread Baking Recipes', score: 75 },
    { id: 4, name: 'Simple One Pot Pasta Cooking Methods', score: 71 },
    { id: 5, name: 'Fresh Garden Salad Combinations For Summer', score: 65 },
    { id: 6, name: 'Grilled Chicken Seasoning Tips And Tricks', score: 45 },
    { id: 7, name: 'Slow Cooker Comfort Food Favorites', score: 25 },
    { id: 8, name: 'Budget Friendly Meal Planning Strategies', score: 12 },
    { id: 9, name: 'Creative Sandwich Wraps And Fillings', score: 2 },
    { id: 10, name: 'Seasonal Fruit Dessert Plating Ideas', score: 1 },
];

const FAKE_LOREM_TEXT = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vestibulum nisl, semper in taco with shrimp asada massa et, rutrum fringilla eros. Suspendisse. Proin sed luctus diam, id porta odio. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vestibulum nisl, semper in taco with shrimp asada massa et, rutrum fringilla eros. Suspendisse. Proin sed luctus diam, id porta odio.';

const FAKE_POSTS: ISocialPost[] = [
    {
        id: 'post-1',
        source: 'Instagram',
        sentiment: 'neutral',
        text: FAKE_LOREM_TEXT,
        date: '2024-09-25',
        engagement: 1234567,
        followersCount: 2952102,
        url: ''
    },
    {
        id: 'post-2',
        source: 'TikTok',
        sentiment: 'neutral',
        text: FAKE_LOREM_TEXT,
        date: '2024-09-25',
        engagement: 678000,
        followersCount: 2950000,
        url: ''
    },
    {
        id: 'post-3',
        source: 'Pinterest',
        sentiment: 'neutral',
        text: FAKE_LOREM_TEXT,
        date: '2024-09-25',
        engagement: 1234567,
        followersCount: 2952102,
        url: ''
    },
    {
        id: 'post-4',
        source: 'Facebook',
        sentiment: 'neutral',
        text: FAKE_LOREM_TEXT,
        date: '2024-09-25',
        engagement: 678000,
        followersCount: 2950000,
        url: ''
    },
    {
        id: 'post-5',
        source: 'Instagram',
        sentiment: 'positive',
        text: FAKE_LOREM_TEXT,
        date: '2024-09-24',
        engagement: 892345,
        followersCount: 1540000,
        url: ''
    },
    {
        id: 'post-6',
        source: 'TikTok',
        sentiment: 'positive',
        text: FAKE_LOREM_TEXT,
        date: '2024-09-24',
        engagement: 2100000,
        followersCount: 4200000,
        url: ''
    },
];

const FAKE_KEY_TERMS: IKeyTerm[] = [
    {
        id: 1,
        name: 'Cookie',
        thumbnailUrl: 'https://images.unsplash.com/photo-1499636136210-6f4ee915583e?w=300&h=300&fit=crop',
        chainId: null,
    },
    {
        id: 2,
        name: 'Berry',
        thumbnailUrl: 'https://images.unsplash.com/photo-1488477181946-6428a0291777?w=300&h=300&fit=crop',
        chainId: null,
    },
    {
        id: 3,
        name: 'Lemon',
        thumbnailUrl: 'https://images.unsplash.com/photo-1590502593747-42a996133562?w=300&h=300&fit=crop',
        chainId: null,
    },
    {
        id: 4,
        name: 'Fruit',
        thumbnailUrl: 'https://images.unsplash.com/photo-1619566636858-adf3ef46400b?w=300&h=300&fit=crop',
        chainId: null,
    },
    {
        id: 5,
        name: 'Cheese',
        thumbnailUrl: 'https://images.unsplash.com/photo-1486297678162-eb2a19b0a32d?w=300&h=300&fit=crop',
        chainId: null,
    },
    {
        id: 6,
        name: 'Shrimp',
        thumbnailUrl: 'https://images.unsplash.com/photo-1599084993091-1cb5c0721cc6?w=300&h=300&fit=crop',
        chainId: null,
    },
    {
        id: 7,
        name: 'Cream',
        thumbnailUrl: 'https://images.unsplash.com/photo-1563729784474-d77dbb933a9e?w=300&h=300&fit=crop',
        chainId: null,
    },
    {
        id: 8,
        name: 'Taco',
        thumbnailUrl: 'https://images.unsplash.com/photo-1551504734-5ee1c4a1479b?w=300&h=300&fit=crop',
        chainId: null,
    },
    {
        id: 9,
        name: 'Pasta',
        thumbnailUrl: 'https://images.unsplash.com/photo-1556761223-4c4282c73f77?w=300&h=300&fit=crop',
        chainId: null,
    },
    {
        id: 10,
        name: 'Avocado',
        thumbnailUrl: 'https://images.unsplash.com/photo-1523049673857-eb18f1d7b578?w=300&h=300&fit=crop',
        chainId: null,
    },
];

function getFakeData(permissionLevel: PermissionLevelEnum): SocialThemesData {
    const postsByTheme: SocialThemesData['postsByTheme'] = {};
    const keywordsByTheme: SocialThemesData['keywordsByTheme'] = {};

    FAKE_THEMES.forEach((theme) => {
        postsByTheme[theme.id] = { byEngagement: FAKE_POSTS, byDate: FAKE_POSTS };
        keywordsByTheme[theme.id] = FAKE_KEY_TERMS;
    });

    return {
        permissionLevel,
        hasData: true,
        themes: FAKE_THEMES,
        postsByTheme,
        keywordsByTheme,
    };
}

function mapPost(post: SocialThemePost): ISocialPost {
    return {
        id: post.id ?? uuid(),
        source: SocialPostsModel.getPostSource(post.source ?? ''),
        sentiment: 'neutral',
        text: post.text ?? '',
        date: post.date ?? '',
        engagement: post.engagement ?? 0,
        url: post.url ?? '',
    };
}

export function mapKeyword(keyword: SocialThemeKeyword, index: number): IKeyTerm {
    const isChainKeyword = keyword.keywordTypeId === SocialKeywordTypeEnum.ChainProfile;

    return {
        id: index,
        name: keyword.name ?? '',
        thumbnailUrl: keyword.thumbnailUrl ?? '',
        chainId: isChainKeyword && keyword.id ? keyword.id : null,
    };
}

function transformData(apiThemes: SocialTheme[]): Omit<SocialThemesData, keyof iPermissionFields> {
    const themes: IConversationTheme[] = apiThemes.map((theme, index) => ({
        id: index,
        name: theme.name ?? '',
        score: theme.score ?? 0,
    }));

    const postsByTheme: SocialThemesData['postsByTheme'] = {};
    const keywordsByTheme: SocialThemesData['keywordsByTheme'] = {};

    apiThemes.forEach((theme, index) => {
        postsByTheme[index] = {
            byEngagement: (theme.postsByEngagement ?? []).map(mapPost),
            byDate: (theme.postsByDate ?? []).map(mapPost),
        };
        keywordsByTheme[index] = (theme.keywords ?? []).map(mapKeyword);
    });

    return { themes, postsByTheme, keywordsByTheme };
}

const DEFAULT_DATA: SocialThemesData = {
    permissionLevel: PermissionLevelEnum.Full,
    hasData: true,
    themes: [],
    postsByTheme: {},
    keywordsByTheme: {},
};

async function getSocialThemes(influencerTypeId?: number | null): Promise<SocialThemesData> {
    try {
        const params: RequestParams = influencerTypeId != null ? { influencerTypeId } : {};
        const response: Response = await http.get(REQUEST_TYPE, params);
        const permissionLevel = PermissionSection.getPermissionLevel(response?.permissionLevel);

        if (permissionLevel !== PermissionLevelEnum.Full) {
            return getFakeData(permissionLevel);
        }

        return {
            permissionLevel,
            hasData: response?.hasData ?? false,
            ...transformData(response?.data?.themes ?? []),
        };
    } catch (error) {
        console.error('Fetch error:', REQUEST_TYPE, error);
        return DEFAULT_DATA;
    }
}

export function useSocialThemes(influencerTypeId?: number | null) {
    return useData(DEFAULT_DATA, getSocialThemes, influencerTypeId);
}
